import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import Quote from '../quote/quote.component';
import Person from '../person/person.component';
import ActionBtn from '../action-btn/action-btn.component';
import LineByLine from '../line-by-line/line-by-line.component';
import useIntersection from '../../hooks/use-intersection';
import PodcastLinks from '../podcast-links/podcast-links.component';
import styles from './podcast-article.module.scss';
import { PodcastArticleInitInterface } from './podcast-article.interface';
import { parseEmbedVideoUrl } from '../article-video/article-video.component';
import { articlePrefix, podcastsPrefix } from '../../constants';

const PodcastArticle: React.FunctionComponent<PodcastArticleInitInterface> = ({
  id,
  className,
  title,
  subTitle,
  quoteMain,
  quoteFirst,
  quoteSecond,

  video,

  personLeft,
  personRight,

  podcastLinks,

  onReadArticleClick,
  onOpenedChange,

  children,
}) => {
  const podcastsId = `${podcastsPrefix}${id}`;
  const billCipherId = `${articlePrefix}${id}`;

  const [isOpenedPodcast, setOpenedPodcast] = useState(window.location.hash === `#${podcastsId}` || window.location.hash === `#${billCipherId}`);

  //#region animations
  const personLeftRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const isPersonLeftVisible = useIntersection({
    ref: personLeftRef,
    options: {
      rootMargin: '33% 0px -30%',
    },
  });

  const personRightRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const isPersonRightVisible = useIntersection({
    ref: personRightRef,
    options: {
      rootMargin: '13% 0px -13%',
    },
  });

  const videoRef = useRef() as React.MutableRefObject<HTMLIFrameElement>;
  const isVideoVisible = useIntersection({
    ref: videoRef,
    options: {
      rootMargin: '10% 0px -10%',
    },
  });

  const actionsRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const isActionsVisible = useIntersection({
    ref: actionsRef,
    options: {
      rootMargin: '40px 0px -40px',
    },
  });
  //#endregion animations

  const sendGA = (btnId: string) => {
    if ((window as any)?.gtag) {
      (window as any).gtag( 'event', 'submit', {
        'event_category' : btnId
      });
    }
  };

  return (
    <div
      className={classNames(
        styles.component,
        className,
        {
          [styles.hasSubtitle]: subTitle,
          [styles.isVideoVisible]: isVideoVisible,
          [styles.isActionsVisible]: isActionsVisible,
          [styles.isPersonLeftVisible]: isPersonLeftVisible,
          [styles.isPersonRightVisible]: isPersonRightVisible,
        }
      )}
    >
      <span className={classNames(styles.subtitle)}>Герои подкаста</span>
      <h2 className={classNames(styles.title)}>
        <LineByLine text={title} />
      </h2>
      {subTitle
          ? <h3 className={classNames(styles.h3)}><LineByLine text={subTitle} /></h3>
          : ''
      }

      <Quote
        className={classNames(styles.mainQuote)}
        text={quoteMain.text}
        author={quoteMain.author}
      />
      <Person
        componentRef={personLeftRef}
        className={classNames(
          styles.person1,
          {
            [styles.isLong]: [personLeft.name, personLeft.position].join().length > 60,
          },
        )}
        name={personLeft.name}
        position={personLeft.position}
        isCustomColor={true}
      />
      <Quote
        className={classNames(styles.quote1)}
        text={quoteFirst.text}
        author={quoteFirst.author}
      />
      <Quote
        className={classNames(
          styles.quote2,
          {
            [styles.quote2_mt]: !subTitle,
          },
        )}
        text={quoteSecond.text}
        author={quoteSecond.author}
      />
      <Person
        componentRef={personRightRef}
        className={classNames(styles.person2)}
        name={personRight.name}
        position={personRight.position}
        isCustomColor={true}
      />

      <iframe
        ref={videoRef}
        className={classNames(styles.video)}
        src={parseEmbedVideoUrl(video)}
        title="Запись подкаста"
        allowFullScreen
      />

      <section
        ref={actionsRef}
        className={classNames(styles.actions)}
      >

        <ActionBtn
          id={billCipherId}
          text="Читать расшифровку"
          onClick={() => {
            onReadArticleClick();
            sendGA(billCipherId);
          }}
        />
        <ActionBtn
          id={podcastsId}
          text="Слушать подкаст"
          iconRotate={isOpenedPodcast ? -90 : 90}
          onClick={() => {
            const newValue = !isOpenedPodcast;

            setOpenedPodcast(newValue);
            onOpenedChange(newValue);
            sendGA(podcastsId);
          }}
        />
      </section>

      <PodcastLinks
        className={styles.links}
        isOpened={isOpenedPodcast}
        links={podcastLinks}
      />

      {children(isOpenedPodcast)}
    </div>
  );
};

export default PodcastArticle;
